/**
 * The device authorization confirmation screen, routed at `/device` as
 * `<page-device>`.
 *
 * `SC-F217`. Story: `SC-S217`. This is the page a human reaches by typing the URL
 * their CLI printed on another machine — `internal/pat`'s `DeviceVerificationPath`
 * — and until it existed, following that URL landed on the app shell with an empty
 * outlet. The API half has been complete and tested since the CLI shipped; the two
 * calls this page makes (`POST /v1/device/lookup`, `POST /v1/device/approve`) are
 * the only two such a screen ever makes.
 *
 * # It must authenticate a PERSON, and the whole design follows from that
 *
 * `01` §3.4's rule is that automation can never mint a step-up grant. Approving a
 * device login causes a long-lived **general** API key to exist, which is exactly
 * what `SC-F211` gates, so approval demands a fresh identity proof — a passkey
 * assertion or an emailed code — on top of a signed-in session. Three consequences
 * are visible in this file:
 *
 *   - The route is **guarded** (`extra-routes.ts`): an anonymous reader is sent to
 *     `/sign-in?next=/device?user_code=…` and comes back with the code intact,
 *     rather than filling in a dialog and being refused at the last click.
 *   - The user code is **never auto-submitted**. A code that arrived in the URL is
 *     looked up — a read that decides nothing — and the human still has to press
 *     Approve. A page that approved on load would be a GET that mints a
 *     credential, which is a link somebody can be sent.
 *   - Approving and denying are **asymmetric**, deliberately. Denial needs no
 *     grant, because it creates nothing; requiring a re-verification to say "no"
 *     would push a reader who does not recognize a request into closing the tab
 *     instead, leaving the flow pending for whoever holds the device code. The
 *     safe answer has to be the cheap one.
 *
 * # What the page shows, and why each fact is on it
 *
 * The lookup returns the client's self-description, the address the flow was
 * started from, the credential lifetime being requested and the time left. The IP
 * is the strongest signal a human has: an unexpected address is the tell that
 * somebody else's CLI is asking. The lifetime is shown because the human is
 * agreeing to a stated duration rather than to "a credential". The client string
 * is untrusted input from the asking end and is rendered as text.
 *
 * # The four states
 *
 * *Loading* is the lookup. *Empty* is the no-code-yet state: the URL carried no
 * `user_code`, so the page asks for the eight characters the CLI printed. *Error*
 * renders the `apierr` code and the `request_id` — and note that a bad, expired or
 * unknown code all answer the same opaque `401 invalid_token`, so the page must not
 * pretend to distinguish them. *Populated* is the pending authorization with its
 * two buttons; a flow that is already `approved`, `denied` or `expired` renders as
 * a decided state with no buttons, because offering one would answer `409`.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "../components/index.js";
import { toast, type BnKvRow } from "../components/index.js";

import { confirmStepUpOtp, listPasskeys, requestStepUpOtp, verifyWithPasskey } from "../api/auth.js";
import { decideDevice, lookupDevice, type DeviceLookup } from "../api/device.js";
import { type ApiError, parseError, supportReference } from "../api/errors.js";
import { passkeyFailureMessage, webauthnSupported } from "../api/webauthn.js";

/** The decision the page is holding behind the verification dialog. Approval is
 * the only one that needs a grant, so denial never enters this state. */
type PendingDecision = "approve" | null;

@customElement("page-device")
export class PageDevice extends LitElement {
  /** The code being decided. Seeded from `?user_code=` and otherwise typed. */
  @state() private code = "";
  @state() private loading = false;
  @state() private lookup: DeviceLookup | null = null;
  @state() private loadError: ApiError | null = null;
  @state() private deciding = false;
  /** The recorded outcome, once this page has written one. Kept separately from
   * `lookup.status` so the terminal copy can say "you just approved this" rather
   than "this was already approved". */
  @state() private outcome: "approved" | "denied" | null = null;

  // ── The step-up dialog (`SC-F011`, `SC-F211`) ─────────────────────────────
  @state() private verifyFor: PendingDecision = null;
  @state() private verifying = false;
  @state() private otpId = "";
  @state() private otpCode = "";
  /** null means "not looked up yet or the lookup failed" — the passkey branch
   * stays visible in that state and retries on click, because hiding a working
   * sign-in method because one request failed is worse than an extra click. */
  @state() private hasPasskeys: boolean | null = null;

  static override styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding-top: clamp(var(--bino-space-lg), 8vh, 6rem);
      font-family: var(--bino-font-sans);
    }
    .card {
      width: 100%;
      max-width: 32rem;
    }
    h1 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
      letter-spacing: -0.01em;
    }
    p {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      line-height: 1.55;
      color: var(--bino-text-secondary);
    }
    .fine {
      font-size: var(--bino-font-size-sm);
    }
    .usercode {
      display: block;
      margin: 0 0 var(--bino-space-md);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-lg);
      letter-spacing: 0.12em;
      color: var(--bino-text);
      user-select: all;
    }
    .warn {
      margin: 0 0 var(--bino-space-md);
      padding: var(--bino-space-sm) var(--bino-space-md);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
      font-size: var(--bino-font-size-sm);
      line-height: 1.55;
    }
    .code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      padding: 0 var(--bino-space-xs);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
      color: var(--bino-text);
    }
    .ref {
      margin: 0;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      user-select: all;
    }
    .actions {
      margin-top: var(--bino-space-lg);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
    }
    .verify-branch {
      margin-bottom: var(--bino-space-sm);
    }
    .verify-or {
      margin: 0 0 var(--bino-space-sm);
      text-align: center;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    // The router matches on pathname, so the page reads its own query string. The
    // parameter name is RFC 8628's `user_code`, which is what the CLI prints and
    // what an operator would guess.
    const fromURL = new URLSearchParams(location.search).get("user_code") ?? "";
    if (fromURL !== "") {
      this.code = fromURL;
      // A LOOKUP on load, never a decision. See the module comment.
      void this.load();
    }
  }

  private async load() {
    const code = this.code.trim();
    if (code === "" || this.loading) return;
    this.loading = true;
    this.loadError = null;
    this.outcome = null;
    try {
      this.lookup = await lookupDevice(code);
    } catch (err) {
      this.lookup = null;
      this.loadError = parseError(err);
    } finally {
      this.loading = false;
    }
  }

  /** decide submits the outcome. `verification` is present only for an approval
   * that has just earned a grant; the server refuses an ungranted approval with
   * `verification_required`, which re-opens the dialog rather than resetting the
   * page (`01` §3.2). */
  private async decide(approve: boolean, verification?: string) {
    if (this.deciding) return;
    this.deciding = true;
    try {
      const res = await decideDevice(this.code.trim(), approve, verification);
      this.outcome = res.status === "approved" ? "approved" : "denied";
      if (this.lookup) this.lookup = { ...this.lookup, status: res.status };
      toast(approve ? "Device login approved" : "Device login denied", approve ? "success" : "info");
    } catch (err) {
      const e = parseError(err);
      if (e.code === "verification_required" || e.code === "verification_invalid") {
        // The grant was missing, already spent or older than five minutes —
        // `Consume` burns the entry even on a mismatch — so the dialog re-opens
        // over an untouched page.
        this.openVerify();
      } else if (e.code === "conflict") {
        // Somebody decided it in another tab, or the CLI gave up. Re-read rather
        // than toasting over a button that can no longer work.
        toast("This device login was already decided.", "warning");
        void this.load();
      } else {
        toast(this.failureText(e), "error");
      }
    } finally {
      this.deciding = false;
    }
  }

  // ── Verification ─────────────────────────────────────────────────────────

  private openVerify() {
    // Codes and grants are single-use, so anything left from a previous round
    // could only produce `otp_invalid`.
    this.otpId = "";
    this.otpCode = "";
    this.verifyFor = "approve";
    void this.ensurePasskeyInfo();
  }

  private closeVerify() {
    if (this.verifying) return;
    this.verifyFor = null;
  }

  private async ensurePasskeyInfo() {
    if (this.hasPasskeys !== null) return;
    if (!webauthnSupported()) {
      this.hasPasskeys = false;
      return;
    }
    try {
      this.hasPasskeys = (await listPasskeys()).length > 0;
    } catch {
      // Left null on purpose; see the field comment.
    }
  }

  /** resumeVerified closes the dialog and re-enters the paused approval with the
   * grant. The action is `create_api_key` and carries no target, because that is
   * the action `POST /v1/device/approve` consumes — approving a device login mints
   * exactly the credential `SC-F211` gates, and the action names no resource. */
  private resumeVerified(grant: string) {
    this.verifyFor = null;
    void this.decide(true, grant);
  }

  private async verifyByPasskey() {
    if (this.verifying) return;
    this.verifying = true;
    try {
      await this.ensurePasskeyInfo();
      const grant = await verifyWithPasskey({ action: "create_api_key" });
      this.resumeVerified(grant);
    } catch (err) {
      // A dismissed authenticator prompt is a cancellation: nothing is said, the
      // dialog stays open and the email branch is still there.
      const message = passkeyFailureMessage(err);
      if (message) toast(message, "error");
    } finally {
      this.verifying = false;
    }
  }

  private async sendCode() {
    if (this.verifying) return;
    this.verifying = true;
    try {
      const { otpId } = await requestStepUpOtp();
      this.otpId = otpId;
      this.otpCode = "";
    } catch (err) {
      toast(this.failureText(parseError(err)), "error");
    } finally {
      this.verifying = false;
    }
  }

  private async confirmCode() {
    const code = this.otpCode.trim();
    if (code === "" || this.otpId === "" || this.verifying) return;
    this.verifying = true;
    try {
      const grant = await confirmStepUpOtp(this.otpId, code, { action: "create_api_key" });
      this.resumeVerified(grant);
    } catch (err) {
      // One uniform `otp_invalid` covers wrong, expired and foreign codes, so
      // there is exactly one failure path to render.
      toast(this.failureText(parseError(err)), "error");
    } finally {
      this.verifying = false;
    }
  }

  // ── Formatting ───────────────────────────────────────────────────────────

  private failureText(err: ApiError): string {
    return err.requestId ? `${err.message} (reference ${err.requestId})` : err.message;
  }

  /** A duration in the units a human reads. The lifetime is days-to-months and
   * the remaining window is minutes, so one formatter covers both. */
  private fmtDuration(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds <= 0) return "expired";
    const day = 86400;
    if (seconds >= day) {
      const days = Math.round(seconds / day);
      return days === 1 ? "1 day" : `${days} days`;
    }
    if (seconds >= 3600) {
      const hours = Math.round(seconds / 3600);
      return hours === 1 ? "1 hour" : `${hours} hours`;
    }
    const minutes = Math.max(1, Math.round(seconds / 60));
    return minutes === 1 ? "1 minute" : `${minutes} minutes`;
  }

  // ── Render ───────────────────────────────────────────────────────────────

  override render() {
    if (this.loading) {
      return html`<bn-card class="card">
        <bn-spinner block size="28" label="Checking this device login"></bn-spinner>
      </bn-card>`;
    }
    if (this.outcome) return this.renderOutcome(this.outcome);
    if (this.loadError) return this.renderError(this.loadError);
    if (!this.lookup) return this.renderCodeEntry();
    if (this.lookup.status !== "pending") return this.renderDecided(this.lookup);
    return html`${this.renderPending(this.lookup)}${this.renderVerifyModal()}`;
  }

  /**
   * The empty state: no code yet. It is the ordinary path for a reader who typed
   * the printed URL without the query string, which is what a URL read aloud
   * produces.
   */
  private renderCodeEntry() {
    return html`<bn-card class="card">
      <h1>Authorize a command-line sign-in</h1>
      <p>
        Enter the code your terminal is showing. It is eight characters and is only valid for a few
        minutes.
      </p>
      <bn-field label="Code from your terminal" required>
        <bn-input
          mono
          autocomplete="off"
          spellcheck="false"
          placeholder="ABCD-EFGH"
          .value=${this.code}
          @input=${(e: CustomEvent<{ value: string }>) => (this.code = e.detail.value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            void this.load();
          }}
        ></bn-input>
      </bn-field>
      <div class="actions">
        <bn-button
          variant="primary"
          block
          ?disabled=${this.code.trim() === ""}
          @click=${() => void this.load()}
        >
          Continue
        </bn-button>
      </div>
      <p class="fine">
        Nothing is granted by looking a code up — this only shows you what is being asked for. You
        approve on the next screen.
      </p>
    </bn-card>`;
  }

  /**
   * The error state. Every lookup failure answers the same opaque
   * `401 invalid_token`, so the copy states the four possibilities rather than
   * claiming one: distinguishing them would turn this page into an oracle for
   * which codes are live, and the uniform answer IS the anti-enumeration control.
   */
  private renderError(err: ApiError) {
    const ref = supportReference(err);
    const opaque = err.code === "invalid_token";
    return html`<bn-card class="card">
      <h1>${opaque ? "That code cannot be used" : "We could not check that code"}</h1>
      <p>${err.message} <span class="code">${err.code}</span></p>
      ${opaque
        ? html`<p class="fine">
            We cannot tell you whether it was mistyped, has expired, was already decided or never
            existed — the answer is deliberately the same for all four, so this page cannot be used
            to discover which sign-ins are waiting. Start the command again to get a fresh code.
          </p>`
        : html`<p class="fine">
            This was a failure to reach Sluice Cloud, not a verdict on the code. Your terminal is
            still waiting; try again in a moment.
          </p>`}
      ${ref ? html`<p class="ref">${ref}</p>` : nothing}
      <div class="actions">
        <bn-button
          variant="secondary"
          block
          @click=${() => {
            this.loadError = null;
            this.lookup = null;
          }}
        >
          Enter a different code
        </bn-button>
      </div>
    </bn-card>`;
  }

  /** A flow somebody else already decided, or one that aged out. No buttons: the
   * server answers `409 conflict` to a second decision, so offering one would be
   * offering a click that cannot work. */
  private renderDecided(lookup: DeviceLookup) {
    return html`<bn-card class="card">
      <bn-empty-state
        icon="✓"
        heading=${`This sign-in was already ${lookup.status}`}
        message="There is nothing left to decide. If it was not you, revoke the credential from the
        API keys screen and check your account's security history."
      ></bn-empty-state>
    </bn-card>`;
  }

  /** The page's own terminal state, after this reader decided. */
  private renderOutcome(outcome: "approved" | "denied") {
    return html`<bn-card class="card">
      ${outcome === "approved"
        ? html`<h1>Approved — return to your terminal</h1>
            <p>
              Your command line will pick the credential up within a few seconds. You can close this
              tab.
            </p>
            <p class="fine">
              The credential now appears on your API keys screen, where you can revoke it at any
              time. Approving it was recorded in your account's security history.
            </p>`
        : html`<h1>Denied</h1>
            <p>
              Nothing was granted. The command line will report that the request was refused rather
              than waiting for it to expire.
            </p>
            <p class="fine">
              If you did not start this sign-in, nobody gained anything — but the attempt was made
              from the address shown on the previous screen, which is worth knowing about.
            </p>`}
    </bn-card>`;
  }

  private renderPending(lookup: DeviceLookup) {
    const rows: BnKvRow[] = [
      { label: "Requested by", value: lookup.client || "an unidentified client" },
      { label: "From address", value: lookup.ip || "unknown" },
      { label: "Credential lifetime", value: this.fmtDuration(lookup.lifetime_seconds) },
      { label: "Decide within", value: this.fmtDuration(lookup.expires_in) },
    ];
    return html`<bn-card class="card">
      <h1>Approve this command-line sign-in?</h1>
      <span class="usercode">${lookup.user_code}</span>
      <p>
        Check that this code matches the one your terminal is showing, and that the details below are
        yours.
      </p>
      <bn-kv .rows=${rows}></bn-kv>
      <p class="warn">
        Approving creates an <strong>API key with your full authority</strong>, valid for
        ${this.fmtDuration(lookup.lifetime_seconds)}. If you did not start this from your own
        terminal, deny it — the address above is where the request came from.
      </p>
      <div class="actions">
        <bn-button
          variant="primary"
          block
          ?loading=${this.deciding && this.verifyFor === null}
          @click=${() => this.openVerify()}
        >
          Approve — verify it is me
        </bn-button>
        <bn-button
          variant="danger"
          block
          ?loading=${this.deciding}
          @click=${() => void this.decide(false)}
        >
          Deny
        </bn-button>
      </div>
      <p class="fine">
        Denying takes one click. Approving needs a passkey or an emailed code first, because it
        creates a credential that outlives this browser session.
      </p>
    </bn-card>`;
  }

  /** The verification dialog. Same two branches and same copy pattern as
   * `page-api-keys`, because it is the same grant for the same action — a client
   * that had to learn two dialogs for one mechanism would be a UI inventing a
   * distinction the API does not make. */
  private renderVerifyModal() {
    const sent = this.otpId !== "";
    const offerPasskey = this.hasPasskeys !== false;
    return html`<bn-modal
      ?open=${this.verifyFor !== null}
      heading="Verify it is you"
      @close=${() => this.closeVerify()}
    >
      <p class="fine">
        ${sent
          ? "We sent a one-time code to your email address. Enter it to continue."
          : "Approving this sign-in creates an API credential that can outlive this browser session, so it needs a fresh check that you are here. The proof is single-use and valid for five minutes."}
      </p>
      ${offerPasskey
        ? html`<div class="verify-branch">
              <bn-button
                variant="secondary"
                block
                ?loading=${this.verifying}
                @click=${() => void this.verifyByPasskey()}
                >Verify with a passkey</bn-button
              >
            </div>
            <p class="verify-or">or</p>`
        : nothing}
      ${sent
        ? html`<bn-field label="One-time code" required>
            <bn-input
              mono
              inputmode="numeric"
              autocomplete="one-time-code"
              placeholder="123456"
              ?disabled=${this.verifying}
              .value=${this.otpCode}
              @input=${(e: CustomEvent<{ value: string }>) => (this.otpCode = e.detail.value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                void this.confirmCode();
              }}
            ></bn-input>
          </bn-field>`
        : nothing}
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeVerify()}>Cancel</bn-button>
      ${sent
        ? html`<bn-button
            slot="footer"
            variant="primary"
            ?loading=${this.verifying}
            ?disabled=${this.otpCode.trim() === ""}
            @click=${() => void this.confirmCode()}
            >Verify and approve</bn-button
          >`
        : html`<bn-button
            slot="footer"
            variant="primary"
            ?loading=${this.verifying}
            @click=${() => void this.sendCode()}
            >Email me a code</bn-button
          >`}
    </bn-modal>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-device": PageDevice;
  }
}
